import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, ShoppingBag, Package, TrendingUp, Plus, ArrowRight, Store, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import ProgressBar from "../components/ProgressBar";
import ShopifyConnectCard from "../components/ShopifyConnectCard";

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="lp-card p-6 lp-card-hover" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="lp-section-label">{label}</div>
        <div className="text-3xl font-semibold tracking-tight mt-3" data-testid={`${testId}-value`}>
          {value}
        </div>
        {hint && <div className="text-xs text-gray-500 mt-2">{hint}</div>}
      </div>
      <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
        <Icon size={18} className="text-gray-700" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.stats(), api.listCampaigns()]);
      setStats(s);
      setCampaigns(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRowActivate = async (id) => {
    try {
      const r = await api.activateCampaign(id);
      toast.success(`Pre-order opened. Notified ${r.notifiedVoters} voter(s).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open pre-order"); }
  };
  const onRowLaunch = async (id) => {
    if (!window.confirm("Launch for full-price sale? It leaves LaunchPad and goes live on your store.")) return;
    try {
      const r = await api.launchForSale(id);
      toast.success(`Launched for sale. Notified ${r.notifiedVoters} voter(s).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not launch"); }
  };

  const onSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.seedDemo();
      toast.success(`Seeded ${r.created} demo campaign(s) from ${r.totalProducts} products`);
      await load();
    } catch {
      toast.error("Could not seed demo data");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="lp-section-label">Merchant dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Launchpad</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-xl">
            Validate demand with launches, auto-flip to pre-orders when threshold is hit, and ship with confidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSeed}
            disabled={seeding}
            className="lp-btn-secondary"
            data-testid="seed-demo-btn"
            title="Create a campaign for every demo product"
          >
            <Sparkles size={16} />
            {seeding ? "Seeding…" : "Seed demo data"}
          </button>
          <Link
            to="/app/rounds"
            className="lp-btn-secondary"
            data-testid="dashboard-rounds-link"
          >
            <Trophy size={16} />
            Launch Rounds
          </Link>
          <Link
            to="/app/campaigns/bulk"
            className="lp-btn-secondary"
            data-testid="dashboard-bulk-preorder-link"
          >
            <Package size={16} />
            Bulk pre-order
          </Link>
          <Link
            to="/app/campaigns/new"
            className="lp-btn-primary"
            data-testid="create-campaign-btn"
          >
            <Plus size={16} />
            New campaign
          </Link>
        </div>
      </div>

      {/* Shopify connection */}
      <ShopifyConnectCard />

      {/* Stat bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          icon={Rocket}
          label="Active campaigns"
          value={stats ? stats.activeCampaigns : "—"}
          hint="Voting or pre-order"
          testId="stat-active-campaigns"
        />
        <StatCard
          icon={TrendingUp}
          label="Supporters today"
          value={stats ? stats.votesToday : "—"}
          hint={stats ? `${stats.totalVotes} all-time` : ""}
          testId="stat-votes-today"
        />
        <StatCard
          icon={ShoppingBag}
          label="Total pre-orders"
          value={stats ? stats.totalPreOrders : "—"}
          hint="Across all campaigns"
          testId="stat-total-preorders"
        />
        <StatCard
          icon={Package}
          label="Pending fulfillments"
          value={stats ? stats.pendingFulfillments : "—"}
          hint="Awaiting shipment"
          testId="stat-pending-fulfillments"
        />
      </div>

      {/* Campaign list */}
      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium tracking-tight">Campaigns</h2>
            <p className="text-xs text-gray-500 mt-1">All launch campaigns across your shop.</p>
          </div>
          <span className="text-xs text-gray-500">{campaigns.length} total</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500 text-sm">Loading campaigns…</div>
        ) : campaigns.length === 0 ? (
          <div className="p-16 text-center" data-testid="campaigns-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mx-auto mb-4">
              <Rocket size={22} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium tracking-tight">No campaigns yet</h3>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
              Pick an upcoming product, set a launch threshold, and let customers tell you what to launch.
            </p>
            <button
              onClick={() => navigate("/app/campaigns/new")}
              className="lp-btn-primary mt-6"
              data-testid="empty-create-campaign-btn"
            >
              <Plus size={16} />
              Create your first campaign
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lp-table" data-testid="campaigns-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Pre-orders</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        {c.productImageUrl && (
                          <img
                            src={c.productImageUrl}
                            alt={c.productTitle}
                            className="w-10 h-10 rounded-lg object-cover border border-gray-100"
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{c.productTitle}</div>
                          <div className="text-xs text-gray-500">
                            Threshold {c.voteThreshold} · Created {new Date(c.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge status={c.status} testId={`row-status-${c.id}`} /></td>
                    <td>
                      <div className="min-w-[160px]">
                        <ProgressBar value={c.currentVoteCount} max={c.voteThreshold} testId={`row-progress-${c.id}`} />
                        <div className="text-xs text-gray-500 mt-1.5">
                          {c.currentVoteCount} / {c.voteThreshold} supporters
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm text-gray-900 font-medium">
                        {c.status === "voting" ? "—" : ""}
                      </span>
                      <PreOrderCount campaignId={c.id} hide={c.status === "voting"} />
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {c.status === "voting" && c.currentVoteCount >= c.voteThreshold && (
                          <>
                            <button
                              onClick={() => onRowActivate(c.id)}
                              className="lp-btn-secondary text-xs"
                              data-testid={`row-preorder-${c.id}`}
                              title="Open pre-order"
                            >
                              Pre-order
                            </button>
                            <button
                              onClick={() => onRowLaunch(c.id)}
                              className="lp-btn-secondary text-xs"
                              data-testid={`row-launch-${c.id}`}
                              title="Launch for full-price sale"
                            >
                              Launch
                            </button>
                          </>
                        )}
                        <Link
                          to={`/storefront/${c.id}`}
                          className="lp-btn-secondary text-xs"
                          data-testid={`row-storefront-${c.id}`}
                        >
                          <Store size={14} /> Storefront
                        </Link>
                        <Link
                          to={`/app/campaigns/${c.id}`}
                          className="lp-btn-primary text-xs"
                          data-testid={`row-manage-${c.id}`}
                        >
                          Manage <ArrowRight size={14} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const PreOrderCount = ({ campaignId, hide }) => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (hide) return;
    api.publicStatus(campaignId).then((s) => setCount(s.preOrderCount));
  }, [campaignId, hide]);
  if (hide) return null;
  return <span className="text-sm text-gray-900 font-medium">{count ?? "—"}</span>;
};
